import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail } from "lucide-react";
import { api } from "@/lib/api";

const SUBSTACK_URL =
    "https://substack.com/@coralbaycatering?r=1wil5o&utm_medium=ios&utm_source=stories&shareImageVariant=blur";

export default function NewsletterSection() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/newsletter/subscribe", {
                email,
                source: "homepage",
            });
            setSuccess(true);
            toast.success(
                data.already_subscribed
                    ? "You're already on the list — opening Substack to confirm."
                    : "You're in! Opening Substack to finish subscribing.",
            );
            // Open Substack in a new tab so they can confirm on her profile
            window.open(SUBSTACK_URL, "_blank", "noopener,noreferrer");
        } catch (err) {
            toast.error(
                err.response?.data?.detail?.[0]?.msg ||
                    err.response?.data?.detail ||
                    "Could not subscribe. Try again?",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            className="bg-forest-700 text-sand-100 py-24 lg:py-28"
            data-testid="newsletter-section"
        >
            <div className="max-w-5xl mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <span className="overline !text-palm-400">The Substack</span>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
                            Recipes & island notes,
                            <br />
                            <em className="italic text-palm-400">straight from the kitchen.</em>
                        </h2>
                        <p className="text-sand-100/75 leading-relaxed mt-5 max-w-md">
                            New menus, what's coming off the boats, and a few of our
                            favorite recipes from the Coral Bay kitchen — delivered
                            occasionally. No spam. No fluff.
                        </p>
                    </div>

                    <div className="lg:col-span-5">
                        <form
                            onSubmit={submit}
                            className="flex flex-col gap-3"
                            data-testid="newsletter-form"
                        >
                            <label className="overline !text-palm-400 flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
                                Subscribe by email
                            </label>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                    data-testid="newsletter-email-input"
                                    className="flex-1 bg-transparent border border-sand-100/30 rounded-sm px-4 py-3.5 text-sand-100 placeholder:text-sand-100/40 focus:outline-none focus:border-sand-100 transition-colors"
                                />
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="newsletter-submit-btn"
                                    className="inline-flex items-center justify-center gap-2 bg-sand-100 text-forest-900 px-6 py-3.5 rounded-sm hover:bg-white transition-colors text-sm tracking-wide disabled:opacity-60"
                                >
                                    {submitting ? "Sending…" : "Subscribe"}
                                    {!submitting && (
                                        <ArrowRight
                                            className="w-4 h-4"
                                            strokeWidth={1.5}
                                        />
                                    )}
                                </button>
                            </div>
                            <p className="text-xs text-sand-100/55 mt-1">
                                {success
                                    ? "Confirm your subscription in the Substack tab that just opened."
                                    : "We'll add you to our list and open Substack so you can confirm."}
                            </p>
                            <a
                                href={SUBSTACK_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="newsletter-substack-link"
                                className="text-xs text-sand-100/70 underline-link mt-1 self-start"
                            >
                                Or visit our Substack directly →
                            </a>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
